/**
 * Running Apps Widget
 *
 * A friendly, card-based view of everything running on the server. Each Docker
 * Compose project is presented as an "app" card with a human name, environment
 * badge, health status, and aggregated + per-service resource usage.
 *
 * Apps are split into two clear sections:
 *   🌐 Websites          — the WordPress sites under /var/www
 *   ⚙️  Platform Services — the apps under /srv/apps (AI Content Creator, etc.)
 *
 * Polls the auth-hub `/api/system/containers` endpoint. Admin-only.
 */

import React from 'react';
import { Card } from './Card';
import { systemApi, getErrorMessage } from '../services/api';
import type { ContainerInfo, ContainerProject, DockerOverview } from '../types';

// Stats are heavier than the plain metrics snapshot (Docker samples CPU over
// ~1s per container), so poll less aggressively than the health widget.
const POLL_INTERVAL_MS = 10000;

type Env = 'production' | 'development' | null;
type AppStatus = 'healthy' | 'warning' | 'down';

// ---------------------------------------------------------------------------
// Presentation metadata — turns raw compose project names into friendly apps.
// ---------------------------------------------------------------------------

/** Curated names/icons for known platform services (keyed by compose project). */
const KNOWN_APPS: Record<string, { name: string; icon: string; env?: Env }> = {
  'aicc-prod': { name: 'AI Content Creator', icon: '🤖', env: 'production' },
  'aicc-dev': { name: 'AI Content Creator', icon: '🤖', env: 'development' },
  'auth-hub': { name: 'Services Gateway & Auth', icon: '🔐' },
  'scraper-translator': { name: 'Scraper Translator', icon: '🌐' },
  'uptime-kuma': { name: 'Uptime Monitoring', icon: '📈' },
};

/** Curated names for the WordPress sites (keyed by domain). */
const WP_NAMES: Record<string, { name: string; icon: string }> = {
  '888gcc.hitsearch.biz': { name: '888 Casino', icon: '🎰' },
  'hard-rock-blog.hitsearch.biz': { name: 'Hard Rock Blog', icon: '🎸' },
};

interface AppMeta {
  name: string;
  icon: string;
  env: Env;
  isWebsite: boolean;
  domain: string | null;
  location: string | null;
}

function titleize(s: string): string {
  return s
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase())
    .trim();
}

function deriveAppMeta(project: ContainerProject): AppMeta {
  const wd = project.workingDir;
  const isWebsite =
    (wd?.startsWith('/var/www') ?? false) ||
    project.containers.some((c) => /wordpress/i.test(c.image));

  if (isWebsite) {
    const domain = wd ? wd.split('/').filter(Boolean).pop() || null : null;
    const known = domain ? WP_NAMES[domain] : undefined;
    return {
      name: known?.name || (domain ? titleize(domain.split('.')[0]) : titleize(project.name)),
      icon: known?.icon || '🌍',
      env: 'production',
      isWebsite: true,
      domain,
      location: wd,
    };
  }

  const known = KNOWN_APPS[project.name];
  let env: Env = known?.env ?? null;
  if (!env) {
    if (/(^|[-_])dev($|[-_])/i.test(project.name)) env = 'development';
    else if (/(^|[-_])prod($|[-_])/i.test(project.name)) env = 'production';
  }
  return {
    name: known?.name || titleize(project.name),
    icon: known?.icon || '📦',
    env,
    isWebsite: false,
    domain: null,
    location: wd,
  };
}

/** Friendly role + tech for a single container, derived from compose service + image. */
function serviceMeta(c: ContainerInfo): { role: string; tech: string } {
  const [repo, tag] = c.image.split(':');
  const img = repo.toLowerCase();
  if (/wordpress/.test(img)) return { role: 'WordPress', tech: tag || 'php-fpm' };
  if (/mariadb/.test(img)) return { role: 'Database', tech: `MariaDB ${tag || ''}`.trim() };
  if (/mysql/.test(img)) return { role: 'Database', tech: `MySQL ${tag || ''}`.trim() };
  if (/mongo/.test(img)) return { role: 'Database', tech: `MongoDB ${tag || ''}`.trim() };
  if (/redis/.test(img)) return { role: 'Cache', tech: `Redis ${tag || ''}`.trim() };
  if (/nginx/.test(img)) return { role: 'Web Server', tech: 'nginx' };

  const svc = (c.service || c.name).toLowerCase();
  if (/(^|-)(api|backend)($|-)/.test(svc)) return { role: 'API', tech: titleize(repo) };
  if (/front/.test(svc)) return { role: 'Frontend', tech: titleize(repo) };
  return { role: titleize(c.service || c.name), tech: c.image };
}

function appStatus(containers: ContainerInfo[]): AppStatus {
  const running = containers.filter((c) => c.state === 'running').length;
  const unhealthy = containers.some((c) => c.health === 'unhealthy');
  if (running === 0) return 'down';
  if (running < containers.length || unhealthy) return 'warning';
  return 'healthy';
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes: number | null): string {
  if (bytes == null || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function pressureColor(percent: number | null): string {
  if (percent == null) return 'var(--text-secondary)';
  if (percent >= 90) return '#dc2626';
  if (percent >= 75) return '#f59e0b';
  return '#10b981';
}

const STATUS_META: Record<AppStatus, { label: string; color: string; bg: string }> = {
  healthy: { label: 'Running', color: '#047857', bg: 'rgba(16,185,129,0.14)' },
  warning: { label: 'Degraded', color: '#b45309', bg: 'rgba(245,158,11,0.16)' },
  down: { label: 'Down', color: '#b91c1c', bg: 'rgba(220,38,38,0.16)' },
};

const ENV_META: Record<'production' | 'development', { label: string; color: string; bg: string }> = {
  production: { label: 'PROD', color: '#1d4ed8', bg: 'rgba(37,99,235,0.14)' },
  development: { label: 'DEV', color: '#92400e', bg: 'rgba(245,158,11,0.16)' },
};

const STATE_DOT: Record<string, string> = {
  running: '#10b981',
  restarting: '#f59e0b',
  paused: '#f59e0b',
  exited: '#9ca3af',
  created: '#9ca3af',
  dead: '#dc2626',
};

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

const Badge: React.FC<{ label: string; color: string; bg: string; dot?: boolean }> = ({
  label,
  color,
  bg,
  dot,
}) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      padding: '2px 9px',
      borderRadius: '9999px',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.02em',
      color,
      backgroundColor: bg,
      whiteSpace: 'nowrap',
    }}
  >
    {dot && (
      <span style={{ height: '7px', width: '7px', borderRadius: '9999px', backgroundColor: color }} />
    )}
    {label}
  </span>
);

const MiniBar: React.FC<{ label: string; percent: number; detail: string }> = ({
  label,
  percent,
  detail,
}) => {
  const color = pressureColor(percent);
  const width = Math.max(2, Math.min(100, percent));
  return (
    <div style={{ flex: '1 1 0', minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: '4px',
        }}
      >
        <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)' }}>
          {label}
        </span>
        <span style={{ fontSize: '12px', fontWeight: 700, color }}>{detail}</span>
      </div>
      <div
        style={{
          height: '6px',
          width: '100%',
          borderRadius: '9999px',
          backgroundColor: 'var(--border-light)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${width}%`,
            borderRadius: '9999px',
            backgroundColor: color,
            transition: 'width 0.5s ease',
          }}
        />
      </div>
    </div>
  );
};

const ServiceRow: React.FC<{ c: ContainerInfo }> = ({ c }) => {
  const meta = serviceMeta(c);
  const dot = STATE_DOT[c.state] || '#9ca3af';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '7px 0',
        borderTop: '1px solid var(--border-light)',
      }}
    >
      <span
        style={{ height: '8px', width: '8px', borderRadius: '9999px', backgroundColor: dot, flexShrink: 0 }}
        title={c.status}
      />
      <div style={{ minWidth: 0, flex: '1 1 auto' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
          {meta.role}
        </div>
        <div
          style={{
            fontSize: '11px',
            color: 'var(--text-secondary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {meta.tech}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0, minWidth: '52px' }}>
        <div style={{ fontSize: '12px', fontWeight: 700, color: pressureColor(c.cpuPercent) }}>
          {c.cpuPercent == null ? '—' : `${c.cpuPercent.toFixed(1)}%`}
        </div>
        <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>CPU</div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0, minWidth: '64px' }}>
        <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)' }}>
          {formatBytes(c.memUsedBytes)}
        </div>
        <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>RAM</div>
      </div>
    </div>
  );
};

const AppCard: React.FC<{ project: ContainerProject }> = ({ project }) => {
  const meta = deriveAppMeta(project);
  const status = appStatus(project.containers);
  const sm = STATUS_META[status];

  const runningContainers = project.containers.filter((c) => c.state === 'running');
  const totalCpu = runningContainers.reduce((sum, c) => sum + (c.cpuPercent || 0), 0);
  const totalMem = runningContainers.reduce((sum, c) => sum + (c.memUsedBytes || 0), 0);
  const runningCount = runningContainers.length;

  return (
    <div
      style={{
        backgroundColor: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderLeft: `4px solid ${sm.color}`,
        borderRadius: '12px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <div style={{ fontSize: '26px', lineHeight: 1 }}>{meta.icon}</div>
        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>
              {meta.name}
            </span>
            {meta.env && (
              <Badge
                label={ENV_META[meta.env].label}
                color={ENV_META[meta.env].color}
                bg={ENV_META[meta.env].bg}
              />
            )}
          </div>
          {meta.domain ? (
            <a
              href={`https://${meta.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '12px',
                color: '#2563eb',
                textDecoration: 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
            >
              {meta.domain} ↗
            </a>
          ) : (
            meta.location && (
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={meta.location}
              >
                {meta.location}
              </div>
            )
          )}
        </div>
        <Badge label={sm.label} color={sm.color} bg={sm.bg} dot />
      </div>

      {/* Aggregate usage */}
      <div style={{ display: 'flex', gap: '16px' }}>
        <MiniBar
          label="CPU"
          percent={totalCpu}
          detail={`${totalCpu.toFixed(1)}%`}
        />
        <MiniBar
          label="Memory"
          percent={Math.min(100, totalMem / (1024 * 1024 * 1024) * 100)}
          detail={formatBytes(totalMem)}
        />
      </div>

      {/* Per-service breakdown */}
      <div>
        <div
          style={{
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--text-secondary)',
            fontWeight: 700,
            marginBottom: '2px',
          }}
        >
          {runningCount}/{project.containers.length} containers up
        </div>
        {project.containers.map((c) => (
          <ServiceRow key={c.id} c={c} />
        ))}
      </div>
    </div>
  );
};

const Section: React.FC<{
  icon: string;
  title: string;
  subtitle: string;
  projects: ContainerProject[];
}> = ({ icon, title, subtitle, projects }) => {
  if (projects.length === 0) return null;
  return (
    <div style={{ marginTop: '28px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '14px' }}>
        <h3 style={{ fontSize: '17px', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
          {icon} {title}
        </h3>
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{subtitle}</span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '16px',
        }}
      >
        {projects.map((p) => (
          <AppCard key={p.name} project={p} />
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main widget
// ---------------------------------------------------------------------------

export const ContainersWidget: React.FC = () => {
  const [data, setData] = React.useState<DockerOverview | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const load = async () => {
      try {
        const overview = await systemApi.getContainers(controller.signal);
        if (cancelled) return;
        setData(overview);
        setError(null);
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        setError(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const timer = setInterval(load, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(timer);
    };
  }, []);

  if (loading && !data) {
    return (
      <Card>
        <div style={{ color: 'var(--text-secondary)' }}>Loading running apps…</div>
      </Card>
    );
  }

  if (error && !data) {
    return (
      <Card>
        <div style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)' }}>
          📦 Running Apps
        </div>
        <div style={{ marginTop: '8px', fontSize: '14px', color: '#dc2626' }}>{error}</div>
      </Card>
    );
  }

  if (!data) return null;

  // Split projects into the two top-level sections, sorting so production apps
  // come before development ones within each section.
  const withMeta = data.projects.map((p) => ({ project: p, meta: deriveAppMeta(p) }));
  const sortByEnv = (a: { meta: AppMeta }, b: { meta: AppMeta }) =>
    (a.meta.env === 'development' ? 1 : 0) - (b.meta.env === 'development' ? 1 : 0);

  const websites = withMeta.filter((x) => x.meta.isWebsite).sort(sortByEnv).map((x) => x.project);
  const services = withMeta.filter((x) => !x.meta.isWebsite).sort(sortByEnv).map((x) => x.project);

  return (
    <Card>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
          📦 Running Apps
        </h2>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Badge label={`${data.totals.running} up`} color="#047857" bg="rgba(16,185,129,0.14)" dot />
          {data.totals.stopped > 0 && (
            <Badge
              label={`${data.totals.stopped} stopped`}
              color="#6b7280"
              bg="var(--border-light)"
            />
          )}
          {data.totals.unhealthy > 0 && (
            <Badge
              label={`${data.totals.unhealthy} unhealthy`}
              color="#b91c1c"
              bg="rgba(220,38,38,0.16)"
              dot
            />
          )}
        </div>
      </div>

      {!data.available ? (
        <div style={{ marginTop: '16px', fontSize: '14px', color: '#f59e0b' }}>
          {data.error || 'Docker is unreachable.'}
        </div>
      ) : (
        <>
          <Section
            icon="🌐"
            title="Websites"
            subtitle={`${websites.length} WordPress ${websites.length === 1 ? 'site' : 'sites'} · /var/www`}
            projects={websites}
          />
          <Section
            icon="⚙️"
            title="Platform Services"
            subtitle={`${services.length} ${services.length === 1 ? 'app' : 'apps'} · /srv/apps`}
            projects={services}
          />
        </>
      )}

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '24px',
          paddingTop: '16px',
          borderTop: '1px solid var(--border-light)',
          fontSize: '12px',
          color: 'var(--text-secondary)',
        }}
      >
        {error ? (
          <span style={{ color: '#f59e0b' }}>Reconnecting…</span>
        ) : (
          <span>Updated {new Date(data.timestamp).toLocaleTimeString()}</span>
        )}
      </div>
    </Card>
  );
};
